import React, { useContext } from 'react';
import SplideSlider from 'src/helpers/commonComponents/SplideSlider';
import {
  ComponentRendering,
  Field,
  ImageField,
  LinkField,
  NextImage,
  Placeholder,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import IconHelper from 'src/helpers/commonComponents/IconHelper';
import LinkHelper from 'src/helpers/commonComponents/LinkHelper';
import useDictionary from 'src/hooks/useDictionary';
import { useTheme } from 'lib/context/ThemeContext';
import { useOcDispatch, useOcSelector } from 'src/redux/ocStore';
import ComponentContext from 'lib/context/ComponentContext';
import { createOrder, retrieveOrder } from 'src/redux/ocCurrentOrder';
import { unwrapResult } from '@reduxjs/toolkit';
import { DeliveryFee } from 'src/pages/api/store/get-store-details';
import { FulfillmentType } from 'src/helpers/Constants';
import { DateTime } from 'luxon';
import { storeInfoStoreDetailTailwindVariant } from 'tailwindVariants/components/storeInfoStoreDetailTailwindVariant';
import clsx from 'clsx';

// Interfaces
interface DataObj {
  addressText: Field<string>;
  franchiseText: Field<string>;
  hoursText: Field<string>;
  jobOpportunitiesText: Field<string>;
  myStoreText: Field<string>;
  myStoreIcon?: { jsonValue?: ImageField };
  offers: { jsonValue: LinkField };
  phoneText: Field<string>;
  shopThisStoreText: Field<string>;
  storeSuffix: Field<string>;
  closedText: Field<string>;
  jobOpportunitiesURL: {
    jsonValue: LinkField;
  };
}
interface StoreInfoProps {
  fields?: {
    data?: {
      datasource: DataObj;
    };
  };
}
interface StoreDetailsProps {
  marktingTileRfkId?: string;
  data?: DataObj;
  hours?: DayHours;
  deliveryFee?: DeliveryFee;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storeDetails?: any;
  rendering?: ComponentRendering;
}

//global store setting
export interface GlobalStoreSettings {
  getGlobalStoreSetting: {
    marketingTileRfkId: Field<string>;
    maximumAmount: Field<string>;
    minimumAmount: Field<string>;
    ecommerceDisabled: {
      jsonValue: {
        value: string;
      };
    };
    ecommerceDisabledRedirect?: {
      jsonValue?: LinkField;
    };
    servicesToExcludeFromServicePanelGlobal?: Field<string>;
  };
}
// Components
const StoreHeadings: React.FC<StoreDetailsProps> = ({ data }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const myStoreData = useOcSelector((state) => state?.storeReducer?.selectedStore);
  const dispatch = useOcDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storeDetails: any = useSitecoreContext()?.sitecoreContext?.route?.fields ?? {};
  const storeId: string = storeDetails?.storeID?.value;
  const { componentContextData, setcomponentContextData } = useContext(ComponentContext);
  const { heading, shopButton, subHeading, headingWrapper, iconWrapper, storeText, myStoreIcon } =
    storeInfoStoreDetailTailwindVariant({
      size: {
        initial: 'mobile',
        lg: 'desktop',
      },
    });
  return (
    <div className={headingWrapper()}>
      <Text
        field={{
          value: `${storeDetails?.storeName?.value || ''} ${data?.storeSuffix?.value || ''}`,
        }}
        tag="p"
        className={heading()}
      />
      {storeDetails?.storeType?.value === '2' && (
        <Text field={data?.franchiseText} tag="p" className={subHeading()} />
      )}
      {myStoreData?.storeId === storeId ? (
        <div className={iconWrapper()}>
          <NextImage className={myStoreIcon()} field={data?.myStoreIcon?.jsonValue} />
          <Text field={data?.myStoreText} tag="p" className={storeText()}></Text>
        </div>
      ) : (
        <>
          {data?.shopThisStoreText?.value && (
            <LinkHelper
              className={shopButton()}
              onClick={async (e) => {
                e.preventDefault();
                if (storeId) {
                  setcomponentContextData({
                    ...componentContextData,
                    storeId: storeId,
                    callBackFunctionAfterStoreChange: async () => {
                      const retrieveStoreOrder = await dispatch(retrieveOrder());
                      const responsesStoreOrder = unwrapResult(retrieveStoreOrder);
                      if (responsesStoreOrder === undefined) {
                        await dispatch(
                          createOrder(
                            localStorage.getItem('selected_fulfillment_Method') ===
                              FulfillmentType?.DFS
                          )
                        );
                        localStorage.removeItem('selected_fulfillment_Method');
                      } else {
                        await dispatch(retrieveOrder());
                      }
                    },
                  });
                }
              }}
              field={{ value: { href: '/', text: data?.shopThisStoreText?.value } }}
            />
          )}
        </>
      )}
    </div>
  );
};

const StoreDetails = ({
  data,
  hours,
  specialDays,
  rendering,
}: {
  data: DataObj;
  hours: DayHours;
  deliveryFee?: DeliveryFee;
  specialDays?: specialDateTypeArray;
  rendering?: ComponentRendering;
}) => {
  const { storeDetailWrapper } = storeInfoStoreDetailTailwindVariant({
    size: {
      initial: 'mobile',
      lg: 'desktop',
    },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storeDetails: any = useSitecoreContext()?.sitecoreContext?.route?.fields ?? {};
  return (
    <div className={storeDetailWrapper()}>
      <LeftSideStoreContent
        data={data}
        hours={hours}
        specialDays={specialDays}
        storeDetails={storeDetails}
      />
      <RightSideStoreContent storeDetails={storeDetails} rendering={rendering} />
    </div>
  );
};

const RightSideStoreContent: React.FC<StoreDetailsProps> = ({ rendering }) => {
  const { rightContentWrapper, storeDetailImage } = storeInfoStoreDetailTailwindVariant({
    size: {
      initial: 'mobile',
      lg: 'desktop',
    },
  });

  return (
    <div className={rightContentWrapper()}>
      <StoreSlider />
      <div className={storeDetailImage()}>
        {rendering && <Placeholder name="store-info-personalise" rendering={rendering} />}
      </div>
    </div>
  );
};

const LeftSideStoreContent = ({
  data,
  hours,
  storeDetails,
  specialDays,
}: {
  data: DataObj;
  hours: DayHours;
  deliveryFee?: DeliveryFee;
  specialDays?: specialDateTypeArray;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storeDetails: any;
}) => {
  const {
    storeDetailHeading,
    storeDetailDescription,
    leftContentWrapper,
    storeDetailLink,
    linkIcon,
    firstStoreDetailLink,
  } = storeInfoStoreDetailTailwindVariant({
    size: {
      initial: 'mobile',
      lg: 'desktop',
    },
  });
  const { getDictionaryValue } = useDictionary();
  const { themeNameUpper } = useTheme();
  const showSpecialHours = (dateTimeObject: { [key: string]: string }) => {
    if (!dateTimeObject) return null;

    const now = DateTime.local();

    const formatTime = (time: string) => DateTime.fromFormat(time, 'HH:mm:ss').toFormat('hh:mm a');
    const formatSpecialDate = (date: string) =>
      DateTime.fromISO(date, { zone: 'utc' }).toFormat('MM/dd');

    const { message, specialDate, storeOpenHour, storeCloseHour, startDate, endDate } =
      dateTimeObject;
    const isClosed = storeOpenHour === storeCloseHour;

    const startDateTime = DateTime.fromISO(startDate);
    const endDateTime = DateTime.fromISO(endDate);

    if (now >= startDateTime && now <= endDateTime) {
      return (
        <>
          <p
            className={storeDetailDescription({
              className: '!font-body-large-bold',
            })}
          >
            {message}&nbsp;
            {'(' + formatSpecialDate(specialDate) + '):'} &nbsp;
            {isClosed ? (
              <Text field={data?.closedText} tag="span" />
            ) : (
              `${formatTime(storeOpenHour)} - ${formatTime(storeCloseHour)}`
            )}
          </p>
        </>
      );
    }
    return <></>;
  };
  return (
    <div className={leftContentWrapper()}>
      <div>
        <Text field={data?.hoursText} tag="p" className={storeDetailHeading()} />
        {Object.keys(hours)?.map((day) => {
          if (!hours[day]) {
            return;
          }
          return (
            <Text
              key={day} // Added key prop for optimization
              field={{ value: `${day}: ${hours[day]}` }} // Access hours using brackets
              tag="p"
              className={storeDetailDescription()}
            />
          );
        })}
        {Array.isArray(specialDays) &&
          specialDays?.map((day: specialDateType) => <>{showSpecialHours(day)}</>)}
      </div>
      <div>
        <Text field={data?.addressText} tag="p" className={storeDetailHeading()} />
        <Text
          field={{ value: getDictionaryValue(themeNameUpper) }}
          tag="p"
          className={storeDetailDescription()}
        />
        {storeDetails?.street1 && (
          <Text field={storeDetails?.street1} tag="p" className={storeDetailDescription()} />
        )}
        <Text
          field={{
            value: `${storeDetails?.city?.value || ''}, ${storeDetails?.state?.value || ''} ${
              storeDetails?.zip?.value || ''
            }`,
          }}
          tag="p"
          className={storeDetailDescription()}
        />
      </div>
      <div>
        <Text field={data?.phoneText} tag="p" className={storeDetailHeading()} />
        <LinkHelper
          field={{ value: { href: `tel:${storeDetails?.phone?.value}` } }}
          className={storeDetailLink()}
        >
          {storeDetails?.phone && <Text field={storeDetails?.phone} tag="p" />}
        </LinkHelper>
      </div>
      <div>
        {data?.offers?.jsonValue?.value?.text && data?.offers?.jsonValue?.value?.href && (
          <LinkHelper field={data?.offers?.jsonValue} className={firstStoreDetailLink()}>
            <>
              <Text field={{ value: data?.offers?.jsonValue?.value?.text }} tag="p" />
              <IconHelper className={linkIcon()} icon={'chevron-right'} />
            </>
          </LinkHelper>
        )}
        {data?.jobOpportunitiesText?.value && (
          <LinkHelper
            field={{
              value: {
                href: storeDetails?.jobsurl?.value,
                target: '_blank',
              },
            }}
            className={storeDetailLink()}
          >
            <>
              {!!storeDetails?.jobsurl?.value && (
                <>
                  <Text field={data?.jobOpportunitiesText} tag="p" />
                  <IconHelper className={linkIcon()} icon={'chevron-right'} />
                </>
              )}
            </>
          </LinkHelper>
        )}
      </div>
    </div>
  );
};

const StoreSlider: React.FC = () => {
  const { sliderImage, sliderWrapper } = storeInfoStoreDetailTailwindVariant({
    size: {
      initial: 'mobile',
      lg: 'desktop',
    },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storeDetails: any = useSitecoreContext()?.sitecoreContext?.route?.fields ?? {};
  return (
    <div className={sliderWrapper()}>
      <SplideSlider
        options={{
          pagination: false,
          arrows: storeDetails?.images?.value?.split('|')?.length > 1,
          speed: 800,
          autoplay: true,
          interval: 5000,
          type: 'loop',
        }}
      >
        {storeDetails?.images?.value?.split('|')?.length > 0 &&
          storeDetails?.images?.value?.split('|').map((image: string, index: number) => {
            return (
              <NextImage
                key={index}
                className={clsx(sliderImage(), 'store-info-image')}
                field={{
                  src: image,
                  width: '518',
                  height: '371',
                  alt: 'sliderImage',
                }}
              />
            );
          })}
      </SplideSlider>
    </div>
  );
};
interface DayHours {
  [key: string]: string;
}

interface DayInfo {
  dayOfWeek: {
    jsonValue: {
      name: string;
    };
  };
  openTime: {
    value: string;
  };
  closeTime: {
    value: string;
  };
}

function convertHours(data: DayInfo[]): DayHours {
  const day_mapping: { [key: string]: string } = {
    '1': 'Sun',
    '2': 'Mon',
    '3': 'Tue',
    '4': 'Wed',
    '5': 'Thu',
    '6': 'Fri',
    '7': 'Sat',
  };

  const hours: DayHours = {};
  data.forEach((result) => {
    const day = day_mapping[result?.dayOfWeek?.jsonValue?.name];
    const open_time = formatTime(result?.openTime?.value, true);
    const close_time = formatTime(result?.closeTime?.value, true);
    if (open_time != close_time) {
      hours[day] = `${open_time} - ${close_time}`;
    }
  });

  return hours;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getInOrder = (dayHours: any) => {
  const orderedDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const formattedDayHours: {
    [key: string]: string;
  } = {};
  let tempStartDay = '';
  let tempEndDay = '';

  orderedDays.forEach((day, index, array) => {
    if (index === 0) {
      tempStartDay = day;
      tempEndDay = day;
    } else {
      if (dayHours[day] === dayHours[tempEndDay]) {
        tempEndDay = day;
      } else {
        if (tempStartDay === tempEndDay) {
          formattedDayHours[tempStartDay] = dayHours[tempStartDay];
        } else {
          formattedDayHours[`${tempStartDay}-${tempEndDay}`] = dayHours[tempStartDay];
        }
        tempStartDay = day;
        tempEndDay = day;
      }
    }

    if (index === array.length - 1) {
      if (tempStartDay === tempEndDay) {
        formattedDayHours[tempStartDay] = dayHours[tempStartDay];
      } else {
        formattedDayHours[`${tempStartDay}-${tempEndDay}`] = dayHours[tempStartDay];
      }
    }
  });
  return formattedDayHours;
};
function formatTime(time: string, addAMandPM?: boolean): string {
  const [hours, minutes] = time.split(':');
  const formattedHours = parseInt(hours, 10) > 12 ? parseInt(hours, 10) - 12 : parseInt(hours, 10);
  const formattedHoursStr = formattedHours.toString().padStart(2, '0');
  return `${formattedHoursStr}:${minutes} ${
    addAMandPM ? (parseInt(hours, 10) >= 12 && parseInt(hours, 10) <= 23 ? 'PM' : 'AM') : ''
  }`;
}
interface specialDateType {
  [key: string]: string;
}
type specialDateTypeArray = specialDateType[];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StoreInfo: React.FC<StoreInfoProps> = ({ fields, rendering }: any) => {
  const { base } = storeInfoStoreDetailTailwindVariant({
    size: {
      initial: 'mobile',
      lg: 'desktop',
    },
  });
  const dayResults: DayInfo[] = fields?.data?.storeData?.children.results?.[0].children.results;
  const specialDays: specialDateTypeArray =
    fields?.data?.storeData?.specialDays?.results[0]?.children?.results?.map(
      (specialDay: {
        fields?: {
          name: string;
          value: string;
        }[];
      }) => {
        const specialDays: { [key: string]: string } = {};
        specialDay?.fields?.forEach((fieldName: { name: string; value: string }) => {
          specialDays[fieldName.name] = fieldName?.value;
        });
        return specialDays;
      }
    );
  const hours: DayHours = getInOrder(convertHours(dayResults));

  return (
    <>
      {fields?.data?.datasource && (
        <div className={base()} id="general-information">
          <StoreHeadings data={fields?.data?.datasource} />
          <StoreDetails
            data={fields?.data?.datasource}
            specialDays={specialDays}
            hours={hours as DayHours}
            rendering={rendering}
          />
        </div>
      )}
    </>
  );
};

export default StoreInfo;
